import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Download, Pencil, Undo2 } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPlanStep,
  CampaignPlanTier,
  CampaignStepRecipient,
  MailingPostMethod,
} from "@shared-core/data/api-types";
import {
  draftStepHint,
  isActivatableNow,
} from "@shared-core/domain/campaign-step-activation";
import { currentTime } from "@shared-core/domain/current-time";
import {
  ADDRESS_SOURCE_LABELS,
  CALL_OUTCOME_LABELS,
  CALL_OUTCOMES,
  MAILING_POST_METHOD_LABELS,
  MAILING_POST_METHODS,
  mailingDeliveryLine,
  mailingPostedLine,
  notIncludedLabel,
  outcomeMixTotal,
} from "@shared-core/domain/direct-mail-campaigns";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../app/components/primitives/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { ActivateStepNow } from "./ActivateStepNow";
import { failureLogFields, readCampaignActionError } from "./actionErrors";
import { PostedPackCell } from "./PostedPackCell";
import { StepTiersEditor } from "./StepTiersEditor";

/** How many of a step's recipients the panel lists before "and N more". */
const RECIPIENTS_SHOWN = 100;

/**
 * A mailing's or call round's detail, shown when its row in the campaign's
 * list of sends is opened (the same place an email send shows its own
 * detail): why it has nobody yet, which tiers it goes to, and who it
 * includes and leaves out (and why). While it is a Draft the list is who it
 * would reach now; once it is Ready or Open, who it was fixed to. A draft
 * mailing offers "Mark ready now" and a draft call round "Open now", which
 * fix its recipients (the daily job that would do it may be paused). A Ready
 * mailing offers its mailing file and "Mark as posted"; a posted one says
 * when and how, and can be put back to Ready until a follow-up has gone
 * out or a pack has been tracked or returned. A posted mailing's recipients
 * each show their pack: its courier tracking number and delivery, and
 * "Returned – bad address".
 */
export function PlanStepDetail({
  campaignId,
  step,
  tiers,
  onChanged,
  onEdit,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  tiers: CampaignPlanTier[];
  onChanged: () => void;
  /** Opens the step's name / date / delete drawer; drafts only. */
  onEdit: () => void;
}) {
  return (
    <div className="space-y-5">
      {step.posted_on && (
        <p className="text-sm font-medium text-foreground">
          {mailingPostedLine(step.posted_on, step.dispatch_method)}
          {mailingDeliveryLine(step) && (
            <span className="font-normal text-muted-foreground">
              {" "}
              · {mailingDeliveryLine(step)}
            </span>
          )}
        </p>
      )}
      <StateNote step={step} />
      {step.state === "draft" && (
        <div className="flex flex-wrap items-center gap-2">
          {isActivatableNow(step.channel) && (
            <ActivateStepNow
              campaignId={campaignId}
              step={step}
              channel={step.channel}
              onChanged={onChanged}
            />
          )}
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="size-4" />
            Edit name, date or delete
          </Button>
        </div>
      )}
      {step.no_recipients_reason && (
        <p className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          No recipients yet – {step.no_recipients_reason}
        </p>
      )}
      {step.channel === "direct_mail" &&
        (step.state === "ready" || step.state === "posted") && (
          <MailingPosting
            campaignId={campaignId}
            step={step}
            onChanged={onChanged}
          />
        )}
      {step.outcome_tally && <OutcomeTally step={step} />}
      {!step.follows_step_id && (
        <StepTiersEditor
          key={`${step.target_tier_ids.join(",")}|${JSON.stringify(step.artwork_codes)}`}
          campaignId={campaignId}
          step={step}
          tiers={tiers}
          onChanged={onChanged}
        />
      )}
      <StepRecipients
        campaignId={campaignId}
        step={step}
        onChanged={onChanged}
      />
    </div>
  );
}

/** Whether the daily auto-activate job is on, read only for a draft
 *  mailing or call round, so its hint names the job's date only when the job
 *  will act. Null until read, or if it can't be: the hint then promises
 *  nothing. */
function useAutoActivationEnabled(isNeeded: boolean): boolean | null {
  const dataAdapter = useDataAdapter();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isNeeded) return;
    let cancelled = false;
    dataAdapter
      .getCampaignAutoActivation()
      .then((state) => {
        if (!cancelled) setEnabled(state.enabled);
      })
      .catch((e) => {
        console.error(
          "marketing.campaign.auto_activation.load.failed",
          failureLogFields(e, {}),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, isNeeded]);
  return enabled;
}

function StateNote({ step }: { step: CampaignPlanStep }) {
  const isMailing = step.channel === "direct_mail";
  const isDraftActivatable =
    step.state === "draft" && isActivatableNow(step.channel);
  const autoActivationEnabled = useAutoActivationEnabled(isDraftActivatable);
  if (step.state === "draft") {
    return (
      <p className="text-sm text-muted-foreground">
        Recipients aren't fixed yet, so the list below may still change.
        {isActivatableNow(step.channel) &&
          ` ${draftStepHint(step.channel, step.date, autoActivationEnabled)}`}
      </p>
    );
  }
  if (step.state === "ready") {
    return (
      <p className="text-sm text-muted-foreground">
        Ready: recipients are fixed. Download the mailing file to pack it, then
        mark it as posted once it has gone. Follow-ups go to the people it
        reached only after it is marked posted.
      </p>
    );
  }
  if (step.state === "posted") {
    return (
      <p className="text-sm text-muted-foreground">
        These people count as mailed, and any follow-up of this mailing goes to
        them.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {isMailing
        ? "Recipients are fixed: these are the people this mailing goes to."
        : "Recipients are fixed and each has a call ticket."}
    </p>
  );
}

/** An open call round's outcomes so far, from its tickets. */
function OutcomeTally({ step }: { step: CampaignPlanStep }) {
  const tally = step.outcome_tally ?? {};
  return (
    <div className="space-y-2">
      <h3 className="font-medium">
        Outcomes so far ({outcomeMixTotal(tally)} of {step.recipient_count}{" "}
        recorded)
      </h3>
      <ul className="grid gap-1 sm:grid-cols-2 text-sm">
        {CALL_OUTCOMES.map((outcome) => (
          <li
            key={outcome}
            className="flex items-center justify-between rounded-md border border-border px-3 py-1.5"
          >
            <span>{CALL_OUTCOME_LABELS[outcome]}</span>
            <span className="tabular-nums font-medium">
              {tally[outcome] ?? 0}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function todayIso(): string {
  return currentTime().toISOString().slice(0, 10);
}

/**
 * A Ready or posted mailing's posting: its mailing file (one row per
 * recipient, with the artwork code of their tier), "Mark as posted" with
 * the date and how it went, and — while no follow-up has gone out — undoing
 * it. The server keeps the rules (file downloaded first, no future date,
 * no undo after a follow-up); the page shows its reason when it refuses.
 */
function MailingPosting({
  campaignId,
  step,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  onChanged: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [downloading, setDownloading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [postedOn, setPostedOn] = useState(() => {
    const today = todayIso();
    return step.date && step.date < today ? step.date : today;
  });
  const [method, setMethod] = useState<MailingPostMethod>("royal_mail");
  const [error, setError] = useState<string | null>(null);
  const [postedOnError, setPostedOnError] = useState<string | null>(null);
  const ids = { campaign_id: campaignId, step_id: step.id };

  async function handleDownload() {
    setDownloading(true);
    setError(null);
    try {
      const file = await dataAdapter.downloadCampaignMailingFile(
        campaignId,
        step.id,
      );
      capabilities.downloadTextFile(file.filename, file.content, "text/csv");
    } catch (e) {
      console.error(
        "marketing.campaign.mailing_file.failed",
        failureLogFields(e, ids),
      );
      setError(
        readCampaignActionError(
          e,
          "Couldn't download the mailing file. Please try again.",
        ).summary,
      );
    } finally {
      setDownloading(false);
    }
  }

  async function handleMarkPosted() {
    setPosting(true);
    setError(null);
    setPostedOnError(null);
    try {
      await dataAdapter.markCampaignMailingPosted(campaignId, step.id, {
        postedOn,
        dispatchMethod: method,
      });
      onChanged();
    } catch (e) {
      console.error(
        "marketing.campaign.mailing_posted.failed",
        failureLogFields(e, ids),
      );
      const refusal = readCampaignActionError(
        e,
        "Couldn't mark this mailing as posted. Please try again.",
      );
      setPostedOnError(refusal.fields.posted_on ?? null);
      setError(refusal.summary);
    } finally {
      setPosting(false);
    }
  }

  async function handleUndo() {
    setUndoing(true);
    setError(null);
    try {
      await dataAdapter.undoCampaignMailingPosted(campaignId, step.id);
      onChanged();
    } catch (e) {
      console.error(
        "marketing.campaign.mailing_post_undo.failed",
        failureLogFields(e, ids),
      );
      setError(
        readCampaignActionError(
          e,
          "Couldn't undo the posting. Please try again.",
        ).summary,
      );
    } finally {
      setUndoing(false);
    }
  }

  const postedOnErrorId = `mailing-posted-on-${step.id}-error`;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={downloading}
          >
            <Download className="size-4" />
            {downloading ? "Preparing…" : "Download mailing file"}
          </Button>
          <span className="text-sm text-muted-foreground">
            One row per person included below, with their address, where it came
            from, and their tier's artwork code.
          </span>
        </div>
      </div>

      {step.state === "ready" && (
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h3 className="font-medium">Mark as posted</h3>
          <p className="text-sm text-muted-foreground">
            Once marked posted, the {step.recipient_count} people in the
            downloaded file count as mailed, and any follow-up goes to them.
            Anyone added since the file was downloaded wasn't in the packs, so
            isn't counted.
          </p>
          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-1.5">
              <Label htmlFor={`mailing-posted-on-${step.id}`}>
                Date posted
              </Label>
              <Input
                id={`mailing-posted-on-${step.id}`}
                type="date"
                value={postedOn}
                max={todayIso()}
                onChange={(e) => setPostedOn(e.target.value)}
                aria-invalid={postedOnError ? true : undefined}
                aria-describedby={postedOnError ? postedOnErrorId : undefined}
                className="w-44"
              />
              {postedOnError && (
                <p
                  id={postedOnErrorId}
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {postedOnError}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label id={`mailing-method-${step.id}-label`}>Sent by</Label>
              <RadioGroup
                value={method}
                onValueChange={(v) => setMethod(v as MailingPostMethod)}
                aria-labelledby={`mailing-method-${step.id}-label`}
                className="flex flex-wrap gap-4"
              >
                {MAILING_POST_METHODS.map((m) => (
                  <div key={m} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={m}
                      id={`mailing-method-${step.id}-${m}`}
                    />
                    <Label
                      htmlFor={`mailing-method-${step.id}-${m}`}
                      className="font-normal"
                    >
                      {MAILING_POST_METHOD_LABELS[m]}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <Button onClick={handleMarkPosted} disabled={posting || !postedOn}>
              {posting ? "Saving…" : "Mark as posted"}
            </Button>
          </div>
        </div>
      )}

      {step.state === "posted" && step.can_undo_post && (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={undoing}
          >
            <Undo2 className="size-4" />
            {undoing ? "Undoing…" : "Undo – not posted yet"}
          </Button>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** One side of a step's recipients (included, or left out), first page. */
function useStepRecipients(step: CampaignPlanStep, included: boolean) {
  const dataAdapter = useDataAdapter();
  const [recipients, setRecipients] = useState<CampaignStepRecipient[] | null>(
    null,
  );
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const res = await dataAdapter.listCampaignStepRecipients(step.id, {
        included,
        pageSize: RECIPIENTS_SHOWN,
      });
      setRecipients(res.items);
      setTotal(res.total);
    } catch (e) {
      console.error(
        "marketing.campaign.step_recipients.load_failed",
        failureLogFields(e, { step_id: step.id }),
      );
      setError(true);
    }
  }, [dataAdapter, step.id, included]);

  const tiersKey = step.target_tier_ids.join(",");
  useEffect(() => {
    void load();
    // Reload when the step's state, tiers or count change after an action.
  }, [load, step.state, step.recipient_count, tiersKey]);

  return { recipients, total, error, reload: load };
}

function StepRecipients({
  campaignId,
  step,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  onChanged: () => void;
}) {
  const included = useStepRecipients(step, true);
  const notIncluded = useStepRecipients(step, false);
  const isMailing = step.channel === "direct_mail";
  const isOpenCallRound = step.channel === "call" && step.state !== "draft";
  const isPostedMailing = isMailing && step.state === "posted";
  // A pack's change moves the plan's delivered / returned counts too.
  const onPackChanged = () => {
    void included.reload();
    onChanged();
  };

  if (included.error || notIncluded.error) {
    return (
      <div className="text-sm">
        <p className="text-red-700 dark:text-red-300">
          Couldn't load who this step goes to.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => {
            void included.reload();
            void notIncluded.reload();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }
  if (included.recipients === null || notIncluded.recipients === null) {
    return <p className="text-sm text-muted-foreground">Loading recipients…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h3 className="font-medium">Recipients ({included.total})</h3>
        {included.recipients.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody is in this {isMailing ? "mailing" : "call round"} yet.
          </p>
        ) : (
          <div className="rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>{isMailing ? "Address" : "Rep"}</TableHead>
                  {isOpenCallRound && <TableHead>Outcome</TableHead>}
                  {isPostedMailing && <TableHead>Pack</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {included.recipients.map((r) => (
                  <TableRow key={r.person_id}>
                    <TableCell className="align-top whitespace-normal">
                      <PersonCell recipient={r} />
                    </TableCell>
                    <TableCell className="align-top whitespace-normal text-sm">
                      {isMailing ? (
                        <AddressCell recipient={r} />
                      ) : (
                        (r.rep_name ?? (
                          <span className="text-muted-foreground">
                            No rep – the ticket goes to the campaign owner
                          </span>
                        ))
                      )}
                    </TableCell>
                    {isOpenCallRound && (
                      <TableCell className="align-top whitespace-normal text-sm">
                        <OutcomeCell recipient={r} />
                      </TableCell>
                    )}
                    {isPostedMailing && (
                      <TableCell className="align-top whitespace-normal">
                        <PostedPackCell
                          key={`${r.person_id}|${r.tracking_number ?? ""}`}
                          campaignId={campaignId}
                          step={step}
                          recipient={r}
                          onChanged={onPackChanged}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <MoreNote shown={included.recipients.length} total={included.total} />
      </div>

      {notIncluded.total > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium">Not included ({notIncluded.total})</h3>
          <ul className="rounded-lg border border-border divide-y divide-border text-sm">
            {notIncluded.recipients.map((r) => (
              <li
                key={r.person_id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <span>
                  <PersonCell recipient={r} />
                </span>
                <span className="text-amber-800 dark:text-amber-300">
                  {r.skip_reason
                    ? notIncludedLabel(step.channel, r.skip_reason)
                    : "Not included"}
                </span>
              </li>
            ))}
          </ul>
          <MoreNote
            shown={notIncluded.recipients.length}
            total={notIncluded.total}
          />
        </div>
      )}
    </div>
  );
}

function MoreNote({ shown, total }: { shown: number; total: number }) {
  if (total <= shown) return null;
  return (
    <p className="text-xs text-muted-foreground">
      Showing the first {shown} of {total}. View people lists everyone.
    </p>
  );
}

function PersonCell({ recipient }: { recipient: CampaignStepRecipient }) {
  return (
    <>
      <Link
        to={paths.marketingPersonDetail({ personId: recipient.person_id })}
        className="font-medium text-foreground hover:text-primary hover:underline"
      >
        {recipient.name}
      </Link>
      <div className="text-xs text-muted-foreground">
        {[recipient.company_name, recipient.tier_name]
          .filter(Boolean)
          .join(" · ")}
      </div>
    </>
  );
}

/** A call round recipient's outcome, linking to their ticket. */
function OutcomeCell({ recipient }: { recipient: CampaignStepRecipient }) {
  if (!recipient.ticket_id) {
    return <span className="text-muted-foreground">Ticket not raised yet</span>;
  }
  return (
    <Link
      to={paths.ticketDetail({ id: recipient.ticket_id })}
      className="text-primary hover:underline"
    >
      {recipient.outcome
        ? CALL_OUTCOME_LABELS[recipient.outcome]
        : "Not recorded yet"}
    </Link>
  );
}

function AddressCell({ recipient }: { recipient: CampaignStepRecipient }) {
  return (
    <>
      <div
        className={
          recipient.address_source === "company"
            ? "font-medium text-amber-800 dark:text-amber-300"
            : "font-medium text-foreground"
        }
      >
        {ADDRESS_SOURCE_LABELS[recipient.address_source]}
      </div>
      {recipient.postal_address && (
        <div className="text-xs text-muted-foreground max-w-72">
          {recipient.postal_address}
        </div>
      )}
    </>
  );
}
